import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';

/** Fill missing orgs only for rooms still present in this snapshot. */
export const completeRoomOrganizations = (
  rooms: Record<RoomRecord['id'], RoomRecord>,
  organizations: Record<Organization['id'], Organization>,
  fallback: Record<Organization['id'], Organization>
): Record<Organization['id'], Organization> => {
  // Each writer owns its snapshot, but an incomplete response cannot discard
  // another writer's successful org read. Never invent membership or restore
  // removed rooms. See multi-writer-registry-completeness (ai.knowledge/platform).
  const complete = { ...organizations };
  for (const room of Object.values(rooms)) {
    const id = room.organizationId;
    if (!complete[id] && fallback[id]) complete[id] = fallback[id];
  }
  return complete;
};

export interface OrganizationVisibilityOptions<O> {
  readonly organizationIds: readonly string[];
  readonly organizations: O[];
  readonly listOrganizations: () => Promise<O[]>;
  readonly orgIdOf: (organization: O) => string | undefined;
  readonly timeoutMs: number;
  readonly pollIntervalMs: number;
  readonly signal?: AbortSignal;
}

/** Recover replica-lagged orgs without allowing a hung request to hold boot. */
export const awaitOrganizationVisibility = async <O>({
  organizationIds,
  organizations,
  listOrganizations,
  orgIdOf,
  timeoutMs,
  pollIntervalMs,
  signal
}: OrganizationVisibilityOptions<O>): Promise<O[]> => {
  const containsAll = (list: O[]): boolean =>
    organizationIds.every((id) => list.some((organization) => orgIdOf(organization) === id));
  if (containsAll(organizations) || signal?.aborted || timeoutMs <= 0) return organizations;

  let current = organizations;
  let stopped = false;
  let stop!: () => void;
  const stoppedPromise = new Promise<undefined>((resolve) => {
    stop = () => {
      stopped = true;
      resolve(undefined);
    };
  });
  const deadlineTimer = setTimeout(stop, timeoutMs);
  signal?.addEventListener('abort', stop, { once: true });

  try {
    while (!stopped) {
      let pollTimer: ReturnType<typeof setTimeout> | undefined;
      await Promise.race([
        new Promise<void>((resolve) => {
          pollTimer = setTimeout(resolve, pollIntervalMs);
        }),
        stoppedPromise
      ]);
      clearTimeout(pollTimer);
      if (stopped) break;

      try {
        // The API owns its transport retries. This outer bound also settles a
        // never-returning request; late responses/rejections cannot publish a
        // retired account. See settle-every-terminal-branch (ai.knowledge/global).
        const next = await Promise.race([listOrganizations(), stoppedPromise]);
        if (stopped || !next) break;
        current = next;
        if (containsAll(current)) return current;
      } catch {
        // Keep the last genuine response through a bounded registry outage.
      }
    }
    return current;
  } finally {
    clearTimeout(deadlineTimer);
    signal?.removeEventListener('abort', stop);
  }
};
